import { useLayoutEffect, useRef, useState, type FC } from 'react'

import { Select, Textarea } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Container } from '@skytech/ui/Container'
import { useMutation, useQuery } from '@tanstack/react-query'
import CloseIcon from 'mdi-react/CloseIcon'
import { useNavigate } from 'react-router-dom'

import { Translation } from '@/app/translations/Translation'
import adminResources from '@/modules/admin/resources'

import queries from '../queries'

export const AppMessagePage: FC = () => {
  const navigate = useNavigate()
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [customer, setCustomer] = useState<{ value: number; label: string } | undefined>()
  const [appMessage, setAppMessage] = useState<string>('')

  const { data: customers = [] } = useQuery(queries.adminUserCustomersList(), async () => {
    const response = await adminResources.getUserCustomersList()
    if (response.length === 1) {
      setCustomer({ value: response[0].id, label: response[0].name })
    }
    return response
  })

  const customerOptions = customers.map(option => ({
    value: option.id,
    label: String(option.name),
  }))

  const { data } = useQuery(
    queries.adminAppMessage(customer?.value),
    async () => {
      if (!customer?.value) throw Error('Company is missing!')
      const response = await adminResources.getAppMessage(customer.value)
      setAppMessage(response?.appMessage || '')
      return response
    },
    {
      enabled: !!customer && !!customer.value,
    }
  )

  const updateAppMessageMutation = useMutation(adminResources.updateAppMessage)

  const recalcRows = () => {
    const el = textareaRef.current
    if (!el) {
      return
    }

    el.rows = 1

    const style = window.getComputedStyle(el)
    let lineHeight = parseInt(style.lineHeight, 10)
    if (isNaN(lineHeight)) {
      const fontSize = parseInt(style.fontSize, 10) || 16
      lineHeight = fontSize * 1.2
    }
    const calculatedRows = Math.ceil((el.scrollHeight + 1) / lineHeight)
    const newRows = calculatedRows < 6 ? 6 : calculatedRows
    el.rows = newRows
  }

  useLayoutEffect(() => {
    recalcRows()
    window.addEventListener('resize', recalcRows)
    return () => window.removeEventListener('resize', recalcRows)
  }, [appMessage])

  return (
    <Container onSwipedLeft={() => navigate('/')}>
      <Container.Header>
        <Translation id="App message" />
      </Container.Header>

      <Select
        className="mt-4 w-full md:w-2/6"
        capitalizeLabel
        id="customer"
        label={
          <>
            <Translation id="Customer" />*
          </>
        }
        options={customerOptions}
        value={customer?.value}
        marginBottom={16}
        onChange={option => {
          const customerOption = option as { value: number; label: string }
          setCustomer(customerOption)
        }}
      />

      <Textarea
        textareaRef={textareaRef}
        capitalizeLabel
        isFullWidth
        id="appMessage"
        marginBottom={16}
        value={appMessage}
        label={<Translation id="Message to employee:" />}
        onChange={e => {
          setAppMessage(e.target.value)
        }}
      />

      <div className="mobile:flex-row flex flex-col gap-3">
        <Button
          isIconRight
          disabled={!appMessage}
          variant="secondary"
          onClick={e => {
            e.preventDefault()
            setAppMessage('')
          }}
          Icon={<CloseIcon />}
        >
          <Translation id="Clear message" />
        </Button>

        <Button
          disabled={
            !customer?.value ||
            appMessage === data?.appMessage ||
            updateAppMessageMutation.isLoading
          }
          variant="primary"
          isLoading={updateAppMessageMutation.isLoading}
          onClick={e => {
            e.preventDefault()
            if (!customer?.value || appMessage === data?.appMessage) {
              return
            }
            updateAppMessageMutation.mutate({
              customerId: customer.value,
              appMessage,
            })
          }}
        >
          <Translation id="Save" />
        </Button>
      </div>
    </Container>
  )
}
